import { useEffect, useMemo, useState } from "react";

/**
 * オファー作成ウィザードの送信先 / ロールホイール（`RadioCardGroup` の arc モード）の
 * カード寸法と縦中心を、実際に空いている領域から算出するフック。
 *
 * ホームのやることホイール（`InProgressWheel`）/ プロジェクトホイール（`ProjectWheel`）は
 * カードサイズを固定値ではなく「viewport から上下のクロームを除いた残り領域」へ
 * 白銀比（1:√2）でフィットさせ、`verticalCenterY` でその領域の縦中央へ寄せている。
 * ウィザードも同じ方式に揃えるための共通ロジック。
 *
 * ウィザード特有の事情として、上下のクロームがページ全体ではなくオーバーレイ側にある:
 *  - 上: オーバーレイ上端（右カラムの上端）+ ステッパーを含むヘッダー + sticky な
 *        絞り込みバー（送信先 = 検索フィールド / ロール = ロールタグ）
 *  - 下: ActionPanel（戻る / 次へ）+ その上に載る副パネル（選択中の詳細プレビュー）
 */

/** 白銀比（カード縦横比 1:√2）。 */
const SILVER_RATIO = Math.SQRT2;
/** カード領域の上下に確保する呼吸余白(px)。 */
const CARD_REGION_PADDING_PX = 16;
/**
 * OverlayBody 上端に sticky する絞り込みバーの想定高さ(px)。
 * 送信先の検索フィールド（`Input type="search"`）とロールタグ（`FilterChipBar`）は
 * どちらも 1 行ぶんで、上下 padding 込みでおおむねこの高さに収まる。
 */
const STICKY_FILTER_BAR_PX = 52;
/** カード高さの下限(px)。右カラムが極端に低くてもこれ以上は縮めない。 */
const MIN_CARD_HEIGHT_PX = 280;
/**
 * カード高さの上限(px)。基準寸法（`OfferUserCard` の 256×362、ポップオーバーと
 * 同じ）の約 1.1 倍。viewport が縦に広いときに空き領域いっぱいまで際限なく
 * 拡大すると、かえって一覧性・視認性が落ちるため、基準からわずかな拡大までで
 * 頭打ちにする。上限で止まったぶんは空き領域の上下余白になる（縦中心は変えない）。
 */
const MAX_CARD_HEIGHT_PX = 400;
/** カード左右に最低限確保する余白(px)。 */
const MIN_HORIZONTAL_INSET_PX = 24;
/** カード幅の下限(px)。 */
const MIN_CARD_WIDTH_PX = 200;
/** レイアウト再計算のジッター抑止しきい値(px)。これ未満の変化は無視する。 */
const LAYOUT_DAMPING_PX = 8;

export interface OfferWheelLayout {
  /** `RadioCardGroup` の `cardSize` に渡す寸法。 */
  cardSize: { width: number; height: number };
  /** `RadioCardGroup` の `verticalCenterY` に渡す viewport Y 座標。 */
  verticalCenterY: number;
}

export interface UseOfferWheelLayoutArgs {
  /** オーバーレイ上端の viewport Y 座標（`getBoundingClientRect().top`）。 */
  overlayTop: number;
  /** オーバーレイ（右カラム）の幅。カード幅の上限に使う。 */
  overlayWidth: number;
  /** ステッパーを含むヘッダーの実高。 */
  headerHeight: number;
  /**
   * viewport 下端から確保する余白。ActionPanel・副パネル・BottomAppBar を
   * 含んだ値（`OfferWizardOverlay` の `bodyPaddingBottom` と同じ系統）。
   */
  reservedBottom: number;
  /** false のときは計算せず `null` を返す（coverflow / list モード）。 */
  enabled: boolean;
}

export const useOfferWheelLayout = ({
  overlayTop,
  overlayWidth,
  headerHeight,
  reservedBottom,
  enabled,
}: UseOfferWheelLayoutArgs): OfferWheelLayout | null => {
  const [viewportHeight, setViewportHeight] = useState(() =>
    typeof window === "undefined" ? 0 : window.innerHeight,
  );
  useEffect(() => {
    const onResize = () => setViewportHeight(window.innerHeight);
    window.addEventListener("resize", onResize, { passive: true });
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /**
   * 副パネルは選択中のロール / ユーザーによって高さが揺れる（説明文の行数など）。
   * 揺れるたびにカードが伸縮すると目障りなので、`ProjectWheel` と同じく
   * 「これまでに観測した最大値」で単調増加させ、最も背の高いパネルに合わせて
   * 一度だけ縮むようにする。
   */
  const [bottomReserve, setBottomReserve] = useState(reservedBottom);
  useEffect(() => {
    setBottomReserve((prev) => Math.max(prev, reservedBottom));
  }, [reservedBottom]);

  const rawLayout = useMemo<OfferWheelLayout | null>(() => {
    if (!enabled || viewportHeight <= 0 || overlayWidth <= 0) return null;

    const topOffset =
      overlayTop + headerHeight + STICKY_FILTER_BAR_PX + CARD_REGION_PADDING_PX;
    const freeHeight =
      viewportHeight - topOffset - (bottomReserve + CARD_REGION_PADDING_PX);

    let cardHeight = Math.min(
      MAX_CARD_HEIGHT_PX,
      Math.max(MIN_CARD_HEIGHT_PX, freeHeight),
    );
    let cardWidth = cardHeight / SILVER_RATIO;
    const maxCardWidth = Math.max(
      MIN_CARD_WIDTH_PX,
      overlayWidth - MIN_HORIZONTAL_INSET_PX * 2,
    );
    if (cardWidth > maxCardWidth) {
      cardWidth = maxCardWidth;
      cardHeight = cardWidth * SILVER_RATIO;
    }

    const verticalCenterY =
      freeHeight > 0 ? topOffset + freeHeight / 2 : viewportHeight / 2;

    return {
      cardSize: {
        width: Math.round(cardWidth),
        height: Math.round(cardHeight),
      },
      verticalCenterY: Math.round(verticalCenterY),
    };
  }, [
    enabled,
    viewportHeight,
    overlayTop,
    overlayWidth,
    headerHeight,
    bottomReserve,
  ]);

  // ジッター抑止: しきい値未満の変化では `RadioCardGroup` へ渡す値を更新しない。
  const [layout, setLayout] = useState(rawLayout);
  useEffect(() => {
    setLayout((prev) => {
      if (!prev || !rawLayout) return rawLayout;
      const stable =
        Math.abs(prev.cardSize.width - rawLayout.cardSize.width) <
          LAYOUT_DAMPING_PX &&
        Math.abs(prev.cardSize.height - rawLayout.cardSize.height) <
          LAYOUT_DAMPING_PX &&
        Math.abs(prev.verticalCenterY - rawLayout.verticalCenterY) <
          LAYOUT_DAMPING_PX;
      return stable ? prev : rawLayout;
    });
  }, [rawLayout]);

  return layout;
};
